import { DATATABLE_DESTROYED, DATATABLE_INITIALIZED, DATATABLE_ROW_RENDERED } from 'cadenza/ui/base-table/base-table-event-types';
import { on, platformModifierKey, unByKey } from 'cadenza/utils/event-util';
import { ResultTablePlugin } from 'cadenza/result-table/result-table-plugin';
import { supportsTouch } from 'cadenza/utils/media';
import { INDEX_COLUMN_INDEX } from 'cadenza/result-table/result-table-index-column-index';

const COMPONENT_NAME = 'd-selection-result-table-plugin';

const SELECTED_CELL_CLASS = 'is-selected';
const CELL_CLASSES_TO_IGNORE_CLICKS_ON = [ 'processing-column', 'column-type-URL' ];

/**
 * A cell selection of the table represented as a Map where the keys are row ids and the values Sets
 * of indices of selected cells in column definition space (including index and invisible columns).
 * The column at cell index {@link INDEX_COLUMN_INDEX}) represents a selection of the whole row
 * if present.
 *
 * @typedef {Map<*, Set<int>>} ResultTableSelection
 */

/**
 * @typedef ResultTableSelectionCellPointer
 * @property {int} rowIndex - The row index of the clicked cell
 * @property {int} cellIndex - The column index of the clicked cell
 * @property {HTMLTableDataCellElement} tdElement - The td element clicked
 */

/**
 * @enum
 */
export const ResultTableCellSelectionChangeType = {
  ADD: 'ADD',
  SUBTRACT: 'SUBTRACT',
  REPLACE: 'REPLACE'
};

/**
 * An event representing a user de-/selection of cells/rows.
 *
 * The selection sent as detail is sent as a live object and can be used to alter the selection.
 * Additionally calling preventDefault() on the event will cancel the selection.
 */
export class ResultTableSelectionChangeEvent extends CustomEvent {

  static TYPE = 'selection:change';

  /**
   * @param {ResultTableSelection} selection - The new selection
   * @param {ResultTableSelection} previousSelection - The previous selection
   * @param {ResultTableCellSelectionChangeType} changeType - The type of change
   * @param {ResultTableSelection} change - A selection containing just the added/removed selections
   * @param {ResultTableSelectionCellPointer} lastClickedCell - The last clicked cell
   */
  constructor (selection, previousSelection, changeType, change, lastClickedCell) {
    super(
      ResultTableSelectionChangeEvent.TYPE, {
        bubbles: true,
        cancelable: true,
        detail: { selection, previousSelection, lastClickedCell, changeType, change }
      });
  }

  /**
   * @return {ResultTableSelection} The new selection
   */
  get selection () { // This getter exists to get type definition for the detail
    return this.detail.selection;
  }

  /**
   * @return {ResultTableSelection} The previous selection
   */
  get previousSelection () { // This getter exists to get type definition for the detail
    return this.detail.previousSelection;
  }

  /**
   * @return {ResultTableCellSelectionChangeType} The type of change of the selection
   */
  get changeType () { // This getter exists to get type definition for the detail
    return this.detail.changeType;
  }

  /**
   * @return {ResultTableSelection} A selection containing just the added/removed selections
   */
  get change () { // This getter exists to get type definition for the detail
    return this.detail.change;
  }

  /**
   * @return {ResultTableSelectionCellPointer} The last clicked cell
   */
  get lastClickedCell () { // This getter exists to get type definition for the detail
    return this.detail.lastClickedCell;
  }

}

/**
 * Allows selection of cells in the table.
 *
 * We opted for a custom selection implementation instead of using the Datatables Select plugin,
 * because of incompatibilities between the "selection" and "scrolling" DataTables plugins and the
 * pagination feature of the table, as well as to have full control about how selection works.
 *
 * Multi-cell select doesn't work well across grouped cells (row span) of different columns. We
 * ignore that here as there is no feature that requires it, nor a definition on how it should look
 * like (It was never defined).
 */
export class SelectionResultTablePlugin extends ResultTablePlugin {

  _eventKeys = [];
  _dataTableBodyEventKeys = [];

  /** @type {ResultTableSelection} */
  _currentSelection = new Map();
  /** @type {ResultTableSelectionCellPointer|null} */
  _lastClickedCell = null;
  /** @type {ResultTableSelectionCellPointer|null} */
  _rangeSelectionStartCell = null;
  /** @type {ResultTableSelection|null} */
  _rangeSelectionStartSelection = null;

  init () {
    this._eventKeys = [
      on(
        this.table,
        DATATABLE_INITIALIZED,
        () => { this._onDataTableInitialized(); }),
      on(
        this.table,
        DATATABLE_DESTROYED,
        () => { this._onResultTableDisconnect(); }),
      on(
        this.table,
        DATATABLE_ROW_RENDERED,
        ({ detail: { row, data } }) => { this._onRowRendered(row, data); })
    ];
  }

  _onDataTableInitialized () {
    this._enableSelection();
  }

  _onResultTableDisconnect () {
    this._disableSelection();
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
  }

  disconnectedCallback () {
    unByKey(this._eventKeys);
    unByKey(this._dataTableBodyEventKeys);
    this._disableSelection();
  }

  _onRowRendered (trElement, data) {
    const selectedCells = this._currentSelection.get(data.id);
    if (selectedCells) { // Check if we have a selection in that trElement
      const tdElements = trElement.children; // Variable for performance, don't inline
      const length = tdElements.length;  // Variable for performance, don't inline
      for (let cellIndex = 0; cellIndex < length; cellIndex++) { // For...i for performance
        const tdElement = tdElements[cellIndex];
        const columnIndex = this.table.getColumnIndex(tdElement);
        if (selectedCells.has(columnIndex)) {
          this._toggleSelectedClass(tdElement, true);
        }
      }
    }
  }

  _enableSelection () {
    this._dataTableBodyEventKeys = [
      on(this.table, 'click', (event) => { this._onCellClick(event); }, { delegate: 'tbody td' }),
      on(this.table, 'mousedown', event => { this._onCellMouseDown(event); }, { delegate: 'tbody td' })
    ];
  }

  _disableSelection () {
    this.removeAllSelections();

    unByKey(this._dataTableBodyEventKeys);

    this._lastClickedCell = null;
    this._rangeSelectionStartCell = null;
    this._rangeSelectionStartSelection = null;
  }

  _onCellMouseDown (event) {
    // Prevent text selection when selecting multiple cells in a range selection with SHIFT and
    // when add or subtract selection is active
    if (event.shiftKey || platformModifierKey(event)) {
      event.preventDefault();
    }
  }

  _onCellClick (event) {
    // Prevent selection when we actually do a selection within a cell
    if (document.getSelection().toString().length) {
      return;
    }

    const cell = this.table.getDataTable().cell(event.target.closest('td'));
    const tdElement = cell.node();

    if (CELL_CLASSES_TO_IGNORE_CLICKS_ON.some(styleClass => tdElement.classList.contains(styleClass))) {
      return;
    }

    const isAddOrSubtractSelection = supportsTouch ? true : platformModifierKey(event); // e.g. Ctrl pressed
    const isRangeSelection = supportsTouch ? false : event.shiftKey;
    const cellIndex = this.table.getColumnIndex(tdElement);
    const row = tdElement.closest('tr');
    const { id: rowId, index: rowIndex } = this._getRowData(row);
    const isCellSelected = this._isCellSelected(tdElement);

    let changeType = isCellSelected
      ? ResultTableCellSelectionChangeType.SUBTRACT
      : ResultTableCellSelectionChangeType.ADD;
    let preserveRangeSelectionStart = false;

    const previousSelection = structuredClone(this._currentSelection);
    const changedCellsMap = new Map();

    const selectCellsCallback = (_cellIndex, _rowId, shouldBeSelected) => {
      selectCell(_cellIndex, _rowId, shouldBeSelected, this._currentSelection); // Aggregating
      selectCell(_cellIndex, _rowId, true, changedCellsMap); // Change
    };

    if (isRangeSelection) { /* Range selection wins over add function (like in Excel 360 and
                               LibreOffice Calc; Excel Desktop is weird) */
      if (this._lastClickedCell) { // Range selection is only possible with a preceding click
        // We may have already made a range selection right before this click that we need to revert
        this._currentSelection = structuredClone(this._rangeSelectionStartSelection);

        const shouldBeSelected = this._isCellSelected(this._rangeSelectionStartCell.tdElement);
        changeType = shouldBeSelected
          ? ResultTableCellSelectionChangeType.ADD
          : ResultTableCellSelectionChangeType.SUBTRACT;

        this._selectMultipleCellsInDifferentRowsAndDifferentColumns(
          rowIndex,
          tdElement,
          cellIndex,
          shouldBeSelected,
          selectCellsCallback);

        // Don't update the range selection start, because we might need it for subsequent range
        // selections: We want to start the next range selection from the same cell.
        preserveRangeSelectionStart = true;
      } else {
        selectCellsCallback(cellIndex, rowId, !isCellSelected);
      }
    } else { // Single cell change
      if (isAddOrSubtractSelection) {
        selectCellsCallback(cellIndex, rowId, !isCellSelected);
      } else {
        changeType = ResultTableCellSelectionChangeType.REPLACE;
        this._removeAllSelectionsFromModel();
        selectCellsCallback(cellIndex, rowId, true);
      }
    }

    this._lastClickedCell = { rowIndex, cellIndex, tdElement };

    const selectionNotPreventedByListeners = this.dispatchEvent(
      new ResultTableSelectionChangeEvent(
        this._currentSelection,
        previousSelection,
        changeType,
        changedCellsMap,
        this._lastClickedCell));

    if (selectionNotPreventedByListeners) {
      this._updateSelectedClasses();

      // Update range selection start if necessary
      if (!preserveRangeSelectionStart) {
        this._markRangeSelectionStart();
      }
    } else {
      this._currentSelection = previousSelection;
    }
  }

  _getRowData (trElement) {
    return this.table.getDataTable().row(trElement).data();
  }

  /**
   * Removes the current selection from model and DOM.
   */
  removeAllSelections () {
    this._removeAllSelectionsFromModel();
    this._removeAllSelectedClasses();
  }

  _removeAllSelectionsFromModel () {
    this._currentSelection = new Map();
  }

  _removeAllSelectedClasses () {
    // All cells including fixed column cells
    const selectedTdElements = this.table.querySelectorAll(`tbody td.${SELECTED_CELL_CLASS}`);

    const length = selectedTdElements.length;  // Variable for performance, don't inline
    for (let i = 0; i < length; i++) { // For...i for performance
      selectedTdElements[i].classList.remove(SELECTED_CELL_CLASS);
    }
  }

  _markRangeSelectionStart () {
    this._rangeSelectionStartCell = this._lastClickedCell;
    this._rangeSelectionStartSelection = structuredClone(this._currentSelection);
  }

  _toggleSelectedClass (tdElement, shouldBeSelected) {
    tdElement.classList.toggle(SELECTED_CELL_CLASS, shouldBeSelected);
  }

  _selectMultipleCellsInDifferentRowsAndDifferentColumns (
    rowIndex,
    tdElement,
    cellIndex,
    shouldBeSelected,
    selectCellCallback) {
    const clickedCellBelowStartCell = rowIndex > this._rangeSelectionStartCell.rowIndex;
    const clickedCellRightOfStartCell = cellIndex > this._rangeSelectionStartCell.cellIndex;
    const diffCellIndex = Math.abs(this._rangeSelectionStartCell.cellIndex - cellIndex);
    let diffRows = Math.abs(this._rangeSelectionStartCell.rowIndex - rowIndex);
    let cellToSelect = tdElement;

    while (diffRows >= 0) {
      const currentRow = cellToSelect.parentElement;
      const currentRowId = this._getRowData(currentRow).id;
      let currentRowDiffCellIndex = diffCellIndex;
      let lastCellSelected = null;

      while (currentRowDiffCellIndex >= 0 && cellToSelect != null) {
        selectCellCallback(this.table.getColumnIndex(cellToSelect), currentRowId, shouldBeSelected);

        lastCellSelected = cellToSelect;
        cellToSelect = clickedCellRightOfStartCell ? cellToSelect.previousSibling : cellToSelect.nextSibling;
        currentRowDiffCellIndex -= 1;
      }

      if (diffRows > 0) {
        const nextRowToSelect = clickedCellBelowStartCell
          ? lastCellSelected.parentElement.previousSibling
          : lastCellSelected.parentElement.nextSibling;
        cellToSelect = nextRowToSelect.childNodes[tdElement.cellIndex];
      }
      diffRows -= 1;
    }
  }

  _isCellSelected (tdElement) {
    return tdElement.classList.contains(SELECTED_CELL_CLASS);
  }

  /**
   * Returns the current selection for read access only.
   * Selection is cloned to avoid complex bugs.
   *
   * @return {ResultTableSelection} The current selection as a deep clone
   */
  get selection () {
    return structuredClone(this._currentSelection);
  }

  /**
   * Updates the current selection to the given one, including an update of all already drawn cells.
   * The given selection is cloned to avoid complex bugs.
   *
   * @param {ResultTableSelection} selection - The new selection to update the current to
   */
  set selection (selection) {
    this._currentSelection = structuredClone(selection);
    this._updateSelectedClasses();
  }

  get lastClickedCell () {
    return this._lastClickedCell;
  }

  _updateSelectedClasses () {
    const dataTable = this.table.getDataTable();
    if (!dataTable) {
      return; // Nothing to update, as the table is not existing/initialized yet
    }

    this._removeAllSelectedClasses();

    // Only add the class to all cells as the model is updated as a whole after
    const currentSelection = this._currentSelection;
    this._forEachRowById(
      currentSelection.keys(),
      $row => {
        const trElement = $row.node();
        const tdElements = trElement.children;
        const cells = currentSelection.get($row.data().id);
        cells.forEach((_, cellIndex) => {
          const visibleCellIndex = dataTable.column(cellIndex).index('visible');
          this._toggleSelectedClass(tdElements[visibleCellIndex], true);
        });
      });
  }

  /**
   * Because datatables has only weird ways to traverse all rows we do it on our own way, using this
   * function, which calls a callback for every row whose id is in the given array.
   *
   * @param {object[]} ids - The keys to iterate
   * @param {function(DataTable.Api):void} callback - Callback function called with each row object
   * @private
   */
  _forEachRowById (ids, callback) {
    const rowIdSet = new Set(ids);
    const that = this;
    this.table.getDataTable().rows().every(function () {
      const $row = this;

      // there might be more than one row with given id
      if (rowIdSet.has($row.data().id)) {
        callback.call(that, $row);
      }
    });
  }

}

/**
 * Marks a cell selected/deselected in the given selection.
 *
 * @param {int} cellIndex - The column index of the cell to (de)select
 * @param {string} rowId - The id identifying the row in the database (hash of its values if not set)
 * @param {boolean} shouldBeSelected - True if the cell is to be selected, false if to be deselected
 * @param {ResultTableSelection} selection - Selection to update
 */
export function selectCell (cellIndex, rowId, shouldBeSelected, selection) {
  let cells = selection.get(rowId);

  if (shouldBeSelected) {
    if (!cells) {
      cells = new Set();
      selection.set(rowId, cells);
    }
    cells.add(cellIndex);
  } else {
    if (cells) {
      const deleted = cells.delete(cellIndex);
      if (deleted && cells.size === 0) {
        selection.delete(rowId);
      }
    }
  }
}

/**
 * Checks whether the given selection is occupying multiple columns.
 *
 * @param {ResultTableSelection} selection - The selection to check
 * @return {boolean} True if the selection occupies multiple columns
 */
export function isMultiColumnSelection (selection) {
  const rows = selection.values();
  const columnIds = new Set();

  for (const cells of rows) {
    if (cells.size > 1) {
      return true; // More than one cell selected in row
    }

    const value = cells.values().next().value;
    columnIds.add(value);

    if (columnIds.size > 1) {
      return true;
    }
  }

  return false;
}

/**
 * Returns an array of rowIds for all rows that contain some selection (not only those with index
 * column cell selected).
 *
 * @param {ResultTableSelection} selection - The selection to analyse
 * @return {number[]} Indices of all rows that have any selection
 */
export function getSelectedColumns (selection) {
  const columnIndices = new Set();

  for (const row of selection.values()) {
    for (const columnIndex of row) {
      columnIndices.add(columnIndex);
    }
  }

  return [ ...columnIndices ];
}

/**
 * Checks whether the given selection includes index column cells only, which would denote it as row
 * selection.
 *
 * @param {ResultTableSelection} selection - The selection to check
 * @return {boolean} True if the selection includes index column cells
 */
export function isRowSelection (selection) {
  return isColumnSelected(selection, INDEX_COLUMN_INDEX)
    && !isMultiColumnSelection(selection);
}

/**
 * Checks whether the given selection includes cells of the column specified with given visible
 * column index.
 *
 * @param {ResultTableSelection} selection - The selection to check
 * @param {int|int[]} columnIndex - The visible column index to check for selected cells of
 * @return {boolean} True if the selection includes cells of the given column
 */
export function isColumnSelected (selection, columnIndex) {
  const columnIndices = Array.isArray(columnIndex) ? columnIndex : [ columnIndex ];
  const rows = selection.values();

  for (const cells of rows) {
    if (columnIndices.some(index => cells.has(index))) {
      return true;
    }
  }

  return false;
}

/**
 * Returns an array of rowIds for all rows that contain some selection (not only those with index
 * column cell selected).
 *
 * @param {ResultTableSelection} selection - The selection to analyse
 * @return {*[]} Row ids of all rows that have any selection
 */
export function getRowsWithSelection (selection) {
  return [ ...selection.keys() ];
}

customElements.define(COMPONENT_NAME, SelectionResultTablePlugin);
